/**
 * Message thread + composer for the chat widget. Stateless: the parent owns
 * the conversation object, the draft text and the send handler.
 */
import { ChatCircleDots, PaperPlaneTilt, Sparkle } from "@phosphor-icons/react";

export function MessageThread({
  conv,
  sending,
  text,
  setText,
  onSend,
  onKeyDown,
  scrollRef,
  inputRef,
}) {
  return (
    <>
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-white"
        data-testid="chat-messages"
      >
        {(!conv || !conv.messages?.length) && (
          <div className="text-center py-8 text-sm text-muted2">
            <ChatCircleDots size={40} className="mx-auto text-klein mb-3 opacity-50" weight="duotone" />
            <p className="font-medium text-ink">Hola 👋</p>
            <p className="mt-1">Escribe tu primera pregunta y te responderé al instante.</p>
          </div>
        )}
        {conv?.messages?.map((m, i) => (
          <MessageBubble key={`${m.created_at}-${i}`} msg={m} />
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="px-3 py-2 bg-zinc-100 text-sm flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-klein/60 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
              <span className="w-1.5 h-1.5 bg-klein/60 rounded-full animate-bounce" style={{ animationDelay: "120ms" }} />
              <span className="w-1.5 h-1.5 bg-klein/60 rounded-full animate-bounce" style={{ animationDelay: "240ms" }} />
            </div>
          </div>
        )}
        {conv?.discount_offered_pct > 0 && (
          <div className="text-center text-[11px] text-[#1B5E20] bg-[#E8F5E9] border border-[#C8E6C9] px-3 py-2">
            ✨ <strong>{conv.discount_offered_pct}%</strong> de descuento personal mencionado por el asistente
          </div>
        )}
      </div>

      <form
        onSubmit={onSend}
        className="px-3 py-3 border-t border-zinc-200 bg-white"
        data-testid="chat-composer"
      >
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            rows={1}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Escribe tu pregunta sobre este producto..."
            className="flex-1 px-3 py-2 text-sm border border-zinc-300 focus:border-klein focus:outline-none resize-none"
            disabled={sending}
            data-testid="chat-input"
          />
          <button
            type="submit"
            disabled={sending || !text.trim()}
            className="p-2 bg-klein text-white hover:bg-klein-dark disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="chat-send"
          >
            <PaperPlaneTilt size={18} weight="fill" />
          </button>
        </div>
        <p className="mt-2 text-[10px] text-muted2 text-center flex items-center justify-center gap-1">
          <Sparkle size={10} weight="fill" /> Respuestas generadas con IA · El vendedor recibe cada mensaje
        </p>
      </form>
    </>
  );
}

function MessageBubble({ msg }) {
  const role = msg.role;
  const isAI = role === "ai";
  const isVisitor = role === "visitor";
  const isAdmin = role === "admin";
  const time = formatTime(msg.created_at);
  const label = isAI ? "Asistente" : isAdmin ? "Vendedor" : null;

  if (isVisitor) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[78%]">
          <div className="px-3 py-2 bg-klein text-white text-sm whitespace-pre-wrap break-words">
            {msg.text}
          </div>
          <div className="text-[10px] text-muted2 mt-0.5 text-right">{time}</div>
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-start">
      <div className="max-w-[78%]">
        {label && (
          <div className="text-[10px] font-semibold text-klein/80 mb-0.5 uppercase tracking-wide">
            {label}
          </div>
        )}
        <div
          className={`px-3 py-2 text-sm whitespace-pre-wrap break-words ${
            isAdmin ? "bg-[#FFF3E0] text-ink border border-[#FFCC80]" : "bg-zinc-100 text-ink"
          }`}
        >
          {msg.text}
        </div>
        <div className="text-[10px] text-muted2 mt-0.5">{time}</div>
      </div>
    </div>
  );
}

function formatTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}
